/**
 * ============================================================
 * MONTE HERMOM | LAUDO ABAS
 * ============================================================
 * ARQUIVO: /src/components/laudo/LaudoAbas.jsx
 *
 * As 6 abas do laudo:
 *   ABERTURA → VISTORIA → AMOSTRAS → ANÁLISE → REVISÃO → FINALIZAÇÃO
 *
 * Recebe statusCodigo (ex: 'EM_INICIACAO') e calcula o estado de cada aba.
 * ============================================================
 */

import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  FileText, ClipboardCheck, BarChart3, Calculator,
  FileSearch, CheckCircle2, Circle, Loader
} from 'lucide-react';

/* ============================================================
   MAPA DE STATUS → ABA ATUAL
   Fonte: tabela tb_status_laudo
   ============================================================ */
const STATUS_PARA_ABA = {
  // Abertura (criação e qualificação)
  EM_INICIACAO:                  'abertura',
  EM_QUALIFICACAO_REQUERENTE:    'abertura',
  FORMALIZADO:                   'abertura',
  EM_ENQUADRAMENTO:              'abertura',
  FINALIDADE_DEFINIDA:           'abertura',
  AGUARDANDO_ACEITE_PROFISSIONAL:'abertura',
  RECUSADO_PELO_PROFISSIONAL:    'abertura',
  CRIADO:                        'abertura',
  EM_COMPOSICAO:                 'abertura',
  VALIDACAO_PARCIAL:             'abertura',
  AGUARDANDO_IMOVEIS:            'abertura',

  // Vistoria
  ACEITO_PELO_RESPONSAVEL_TECNICO:'vistoria',
  REGIAO_EM_ANDAMENTO:           'vistoria',
  AGENDAMENTO_EM_ANDAMENTO:      'vistoria',
  BLOQUEADO_PARA_VISTORIA:       'vistoria',
  VISTORIA_LIBERADA:             'vistoria',
  EM_VISTORIA:                   'vistoria',
  EM_ELABORACAO_TECNICA:         'vistoria',

  // Demais fases (quando o backend passar a gravá-las)
  AMOSTRAS_EM_ANDAMENTO:         'amostras',
  ANALISE_EM_ANDAMENTO:          'analise',
  REVISAO_EM_ANDAMENTO:          'revisao',
  CONCLUIDO:                     'finalizacao',
  ENCERRADO:                     'finalizacao',
};

const ABAS = [
  { id: 'abertura',    label: 'Abertura',    icone: FileText },
  { id: 'vistoria',    label: 'Vistoria',    icone: ClipboardCheck },
  { id: 'amostras',    label: 'Amostras',    icone: BarChart3 },
  { id: 'analise',     label: 'Análise',     icone: Calculator },
  { id: 'revisao',     label: 'Revisão',     icone: FileSearch },
  { id: 'finalizacao', label: 'Finalização', icone: CheckCircle2 },
];

/* ============================================================
   Calcula o status de cada aba com base no statusCodigo atual
   Retorna 'concluida' | 'andamento' | 'pendente'
   ============================================================ */
function calcularStatusAba(abaId, statusCodigo) {
  const abaAtual = STATUS_PARA_ABA[statusCodigo] || 'abertura';
  const idxAtual = ABAS.findIndex(a => a.id === abaAtual);
  const idxAba   = ABAS.findIndex(a => a.id === abaId);

  if (idxAba < idxAtual) return 'concluida';
  if (idxAba === idxAtual) return 'andamento';
  return 'pendente';
}

function StatusIcon({ status }) {
  if (status === 'concluida') return <CheckCircle2 size={12} className="text-emerald-400" />;
  if (status === 'andamento') return <Loader size={12} className="text-amber-400 animate-spin" />;
  return <Circle size={12} className="text-slate-600" />;
}

function corBolinha(status) {
  if (status === 'concluida') return 'bg-emerald-400';
  if (status === 'andamento') return 'bg-amber-400';
  return 'bg-slate-600';
}

export default function LaudoAbas({ codigo, statusCodigo }) {
  const params = useParams();
  const codigoLaudo = codigo || params.codigo;

  return (
    <nav className="
      border-b border-white/[0.06]
      bg-[#0A0F1E]/60
      px-8
      overflow-x-auto
    ">
      <div className="flex items-center gap-1 min-w-max">

        {ABAS.map((aba, idx) => {
          const Icone  = aba.icone;
          const status = calcularStatusAba(aba.id, statusCodigo);

          return (
            <React.Fragment key={aba.id}>

              <NavLink
                to={`/laudos/${codigoLaudo}/${aba.id}`}
                className={({ isActive }) => `
                  flex items-center gap-2
                  px-4 py-3
                  rounded-lg
                  text-[11px] font-bold uppercase tracking-wider
                  transition-all duration-150
                  ${isActive
                    ? 'bg-amber-400/10 text-amber-400 border-b-2 border-amber-400'
                    : 'text-slate-500 hover:text-white hover:bg-white/[0.03]'
                  }
                `}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${corBolinha(status)}`} />
                <Icone size={14} />
                <span>{aba.label}</span>
                <StatusIcon status={status} />
              </NavLink>

              {idx < ABAS.length - 1 && (
                <span className="text-slate-700 text-[10px]">›</span>
              )}

            </React.Fragment>
          );
        })}

      </div>
    </nav>
  );
}